import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { fetchIntegrations, saveIntegration, deleteIntegration, fetchCsvUploads, fetchIntegrationHealth, fetchIntegrationLogs } from '../../../services/Http/Api';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import MainButton from '../../../shared/layout/buttons/MainButton';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import {
    StyledInput,
    StyledLabel,
    ErrorText,
    FormGroup
} from '../../../shared/layout/forms/FormElements';
import EmptyState from '../../../shared/layout/feedback/EmptyState';
import StandardModal from '../../../shared/layout/modals/StandardModal';

const IntegrationsSettings = () => {
    const { t } = useTranslation();
    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const [integrations, setIntegrations] = useState([]);
    const [csvUploads, setCsvUploads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [integrationToDelete, setIntegrationToDelete] = useState(null);
    const [editingIntegration, setEditingIntegration] = useState(null);
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [healthData, setHealthData] = useState(null);
    const [logsData, setLogsData] = useState([]);
    const [showCredentialsModal, setShowCredentialsModal] = useState(false);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsError, setLogsError] = useState('');
    const logsRequest = useRef(0);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:6001/api';
    const API_BASE_URL = `${API_URL}/v1/integration`;

    const normalizeIntegrator = (row) => {
        if (!row) return row;
        let config = row.config;
        if (typeof config === 'string') {
            try {
                config = JSON.parse(config);
            } catch (_) {
                config = {};
            }
        }
        return {
            ...row,
            config: config && typeof config === 'object' ? config : {},
        };
    };

    const normalizeIntegrations = (rows) => {
        if (!Array.isArray(rows)) return [];
        return rows.map(normalizeIntegrator);
    };

    const normalizeCsvUploads = (rows) => Array.isArray(rows) ? rows : [];

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [integrationsData, csvUploadsData] = await Promise.all([
                fetchIntegrations(),
                fetchCsvUploads(),
            ]);
            setIntegrations(normalizeIntegrations(integrationsData));
            setCsvUploads(normalizeCsvUploads(csvUploadsData));
        } catch (err) {
            toast.error(t('Settings.Integrations.error') + ': ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const onSubmit = async (data) => {
        try {
            const { config, ...payload } = data;
            // Metadata-only edits must not replace config fields omitted by the API.
            if (!editingIntegration || (config?.endpoint || '') !== (editingIntegration.config?.endpoint || '')) {
                payload.config = { ...editingIntegration?.config, ...config };
            }
            await saveIntegration(payload);
            await loadData();
            reset({
                name: '',
                email: '',
                venue_id: '',
                config: { endpoint: '' },
            });
            setEditingIntegration(null);
            toast.success(editingIntegration ? t('Settings.Integrations.saveSuccess') : t('Settings.Integrations.saveSuccess'));
        } catch (err) {
            toast.error(t('Settings.Integrations.saveError') + ': ' + err.message);
        }
    };

    const handleToggleActive = async (id, isActive) => {
        try {
            await saveIntegration({ id, is_active: !isActive });
            setIntegrations(prev =>
                prev.map(integration =>
                    integration.id === id ? { ...integration, is_active: !isActive } : integration
                )
            );
            toast.success(t('Settings.Integrations.statusSuccess'));
        } catch (err) {
            toast.error(t('Settings.Integrations.statusError') + ': ' + err.message);
        }
    };

    const handleDeleteIntegrator = async (id) => {
        const selected = integrations.find((integration) => integration.id === id);
        setIntegrationToDelete(selected || { id });
    };

    const handleEditIntegrator = (integration) => {
        const normalized = normalizeIntegrator(integration);
        setEditingIntegration(normalized);
        reset({
            id: normalized.id,
            name: normalized.name || '',
            email: normalized.email || '',
            venue_id: normalized.venue_id || '',
            config: {
                endpoint: normalized.config?.endpoint || '',
            },
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingIntegration(null);
        reset({
            name: '',
            email: '',
            venue_id: '',
            config: { endpoint: '' },
        });
    };

    const confirmDeleteIntegrator = async () => {
        if (!integrationToDelete?.id) return;
        try {
            await deleteIntegration(integrationToDelete.id);
            setIntegrations(prev => prev.filter(i => i.id !== integrationToDelete.id));
            setIntegrationToDelete(null);
            toast.success(t('Settings.Integrations.deleteSuccess'));
        } catch (err) {
            toast.error(t('Settings.Integrations.deleteError') + ': ' + err.message);
        }
    };

    const handleViewCredentials = (integration) => {
        setSelectedIntegration(normalizeIntegrator(integration));
        setShowCredentialsModal(true);
    };

    const handleViewLogs = async (integration) => {
        const request = ++logsRequest.current;
        setSelectedIntegration(normalizeIntegrator(integration));
        setShowLogsModal(true);
        setLogsLoading(true);
        setLogsData([]);
        setLogsError('');
        try {
            const logs = await fetchIntegrationLogs(integration.id);
            if (request !== logsRequest.current) return;
            setLogsData(logs?.data || []);
        } catch (err) {
            if (request !== logsRequest.current) return;
            setLogsError(t('Settings.Integrations.error'));
        } finally {
            if (request === logsRequest.current) setLogsLoading(false);
        }
    };

    const handleViewHealth = async (integration) => {
        try {
            const health = await fetchIntegrationHealth(integration.id);
            setHealthData(health);
            setSelectedIntegration(normalizeIntegrator(integration));
        } catch (err) {
            toast.error('Failed to fetch health status');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success(t('Settings.Integrations.copiedToClipboard'));
    };

    const getIntegrationTypeBadge = (type) => {
        const colors = {
            shopware: { bg: '#dbeafe', color: '#1e40af' },
            woocommerce: { bg: '#fce7f3', color: '#9d174d' },
            generic: { bg: '#e5e7eb', color: '#374151' },
            default: { bg: '#f3e8ff', color: '#7c3aed' },
        };
        const style = colors[type] || colors.default;
        return (
            <TypeBadge style={{ background: style.bg, color: style.color }}>
                {type || 'generic'}
            </TypeBadge>
        );
    };

    const customStyles = {
        table: { style: { backgroundColor: 'transparent' } },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                minHeight: '48px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        rows: {
            style: {
                minHeight: '60px',
                fontSize: '14px',
                color: '#111827',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #f3f4f6',
                },
                '&:hover': {
                    backgroundColor: '#f9fafb',
                    cursor: 'default',
                },
            },
        },
    };

    const integrationsColumns = [
        {
            name: t('Settings.Integrations.name'),
            selector: row => row.name,
            sortable: true,
            cell: row => (
                <NameCell>
                    <span>{row.name}</span>
                    {getIntegrationTypeBadge(row.integration_type)}
                </NameCell>
            ),
        },
        {
            name: t('Settings.Integrations.email'),
            selector: row => row.email,
            sortable: true,
        },
        {
            name: t('Settings.Integrations.apiKey'),
            selector: row => row.api_key || '—',
            cell: row => (
                <ApiKeyCell>
                    <code>{row.api_key ? `${row.api_key.slice(0, 12)}...` : '—'}</code>
                    {row.api_key && (
                        <CopyButton onClick={() => copyToClipboard(row.api_key)} title="Copy API Key">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                        </CopyButton>
                    )}
                </ApiKeyCell>
            ),
            sortable: false,
        },
        {
            name: t('Settings.Integrations.status'),
            selector: row => row.is_active,
            sortable: true,
            cell: row => (
                <StatusBadge $active={row.is_active}>
                    {row.is_active ? t('Settings.Integrations.active') : t('Settings.Integrations.inactive')}
                </StatusBadge>
            )
        },
        {
            name: t('Settings.Integrations.actions'),
            cell: row => (
                <ActionsContainer>
                    <IconButton onClick={() => handleViewCredentials(row)} title="API Documentation">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                            <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                    </IconButton>
                    <IconButton onClick={() => handleViewLogs(row)} title="View Logs">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="12" y1="18" x2="12" y2="12"></line>
                            <line x1="9" y1="15" x2="15" y2="15"></line>
                        </svg>
                    </IconButton>
                    <IconButton onClick={() => handleEditIntegrator(row)} title={t('common.edit')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                    </IconButton>
                    <IconButton
                        onClick={() => handleToggleActive(row.id, row.is_active)}
                        title={row.is_active ? t('Settings.Integrations.deactivate') : t('Settings.Integrations.activate')}
                        $variant={row.is_active ? 'warning' : 'success'}
                    >
                        {row.is_active ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                        )}
                    </IconButton>
                    <IconButton onClick={() => handleDeleteIntegrator(row.id)} title={t('common.delete')} $variant="danger">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                    </IconButton>
                </ActionsContainer>
            ),
            width: '200px',
        }
    ];

    const csvColumns = [
        {
            name: t('Settings.Integrations.fileName'),
            selector: row => row.file_name,
            sortable: true,
        },
        {
            name: t('Settings.Integrations.status'),
            selector: row => row.status,
            sortable: true,
        },
        {
            name: t('Settings.Integrations.processedAt'),
            selector: row => new Date(row.processed_at).toLocaleString(),
            sortable: true,
        },
        {
            name: t('Settings.Integrations.errors'),
            selector: row => row.error_details,
            cell: row => (
                <span title={JSON.stringify(row.error_details)}>
                    {row.error_details ? t('Settings.Integrations.hasErrors') : t('Settings.Integrations.noErrors')}
                </span>
            )
        }
    ];

    if (loading) return <LoadingMessage>{t('Settings.loading')}</LoadingMessage>;

    return (
        <Container>
            <StyledCard title={t('Settings.Integrations.addIntegration')}>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    {editingIntegration && (
                        <div style={{
                            marginBottom: 4,
                            padding: '0.625rem 0.75rem',
                            borderRadius: 10,
                            border: '1px solid var(--color-primary, #821692)',
                            background: 'rgba(130, 22, 146, 0.06)',
                            fontWeight: 600,
                            color: 'var(--color-primary, #821692)',
                        }}>
                            {t('common.edit')}: {editingIntegration.name || editingIntegration.id}
                        </div>
                    )}
                    <input type="hidden" {...register('id')} />
                    <Grid>
                        <FormGroup>
                            <StyledLabel>{t('Settings.Integrations.name')}</StyledLabel>
                            <StyledInput
                                {...register('name', { required: t('Settings.Integrations.nameRequired') })}
                                placeholder={t('Settings.Integrations.namePlaceholder')}
                                $error={!!errors.name}
                            />
                            {errors.name && <ErrorText>{errors.name.message}</ErrorText>}
                        </FormGroup>
                        <FormGroup>
                            <StyledLabel>{t('Settings.Integrations.email')}</StyledLabel>
                            <StyledInput
                                {...register('email', { pattern: { value: /^\S+@\S+\.\S+$/, message: t('Settings.Integrations.invalidEmail') } })}
                                placeholder={t('Settings.Integrations.emailPlaceholder')}
                                $error={!!errors.email}
                            />
                            {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
                        </FormGroup>
                        <FormGroup>
                            <StyledLabel>{t('Settings.Integrations.apiEndpoint')}</StyledLabel>
                            <StyledInput
                                {...register('config.endpoint', { pattern: { value: /^https?:\/\//, message: t('Settings.Integrations.invalidUrl') } })}
                                placeholder={t('Settings.Integrations.apiEndpointPlaceholder')}
                                $error={!!errors.config?.endpoint}
                            />
                            {errors.config?.endpoint && <ErrorText>{errors.config.endpoint.message}</ErrorText>}
                        </FormGroup>
                        <FormGroup>
                            <StyledLabel>{t('Settings.Integrations.venueId')}</StyledLabel>
                            <StyledInput
                                {...register('venue_id')}
                                placeholder={t('Settings.Integrations.venueIdPlaceholder')}
                            />
                        </FormGroup>
                    </Grid>
                    <FormFooter>
                        {editingIntegration && (
                            <MainButton type="button" variant="secondary" onClick={handleCancelEdit} style={{ marginRight: 8 }}>
                                {t('common.cancel')}
                            </MainButton>
                        )}
                        <MainButton type="submit">
                            {editingIntegration ? t('common.save') : t('Settings.Integrations.addIntegration')}
                        </MainButton>
                    </FormFooter>
                </Form>
            </StyledCard>

            <StyledCard title={t('Settings.Integrations.currentIntegrations')} noPadding>
                <DataTable
                    columns={integrationsColumns}
                    data={integrations}
                    customStyles={customStyles}
                    pagination
                    noDataComponent={<EmptyState>{t('Settings.Integrations.noIntegrations')}</EmptyState>}
                />
            </StyledCard>

            <StyledCard title={t('Settings.Integrations.csvUploadHistory')} noPadding>
                <DataTable
                    columns={csvColumns}
                    data={csvUploads}
                    customStyles={customStyles}
                    pagination
                    noDataComponent={<EmptyState>{t('Settings.Integrations.noCsvUploads')}</EmptyState>}
                />
            </StyledCard>
            <StandardModal
                isOpen={Boolean(integrationToDelete)}
                onClose={() => setIntegrationToDelete(null)}
                title={t('Settings.Integrations.deleteTitle')}
                footer={(
                    <>
                        <MainButton variant="secondary" onClick={() => setIntegrationToDelete(null)}>
                            {t('common.cancel')}
                        </MainButton>
                        <MainButton variant="danger" onClick={confirmDeleteIntegrator}>
                            {t('common.delete')}
                        </MainButton>
                    </>
                )}
            >
                <p style={{ margin: 0 }}>
                    {t('Settings.Integrations.deleteConfirm')}
                </p>
            </StandardModal>

            {/* API Credentials Modal */}
            <StandardModal
                isOpen={showCredentialsModal}
                onClose={() => setShowCredentialsModal(false)}
                title={`API Credentials: ${selectedIntegration?.name || ''}`}
            >
                {selectedIntegration && (
                    <>
                        <CredentialsSection>
                            <h4>Authentication</h4>
                            <CredentialRow>
                                <CredentialLabel>API Key</CredentialLabel>
                                <CredentialValue>
                                    <code>{selectedIntegration.api_key || '—'}</code>
                                    {selectedIntegration.api_key && (
                                        <CopyButton onClick={() => copyToClipboard(selectedIntegration.api_key)} title="Copy">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                            </svg>
                                        </CopyButton>
                                    )}
                                </CredentialValue>
                            </CredentialRow>
                            <CredentialRow>
                                <CredentialLabel>Integration Type</CredentialLabel>
                                <CredentialValue>
                                    {getIntegrationTypeBadge(selectedIntegration.integration_type)}
                                </CredentialValue>
                            </CredentialRow>
                        </CredentialsSection>

                        <CredentialsSection>
                            <h4>Base URL</h4>
                            <CredentialRow>
                                <CredentialLabel>API Endpoint</CredentialLabel>
                                <CredentialValue>
                                    <code>{API_BASE_URL}</code>
                                    <CopyButton onClick={() => copyToClipboard(API_BASE_URL)} title="Copy">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                        </svg>
                                    </CopyButton>
                                </CredentialValue>
                            </CredentialRow>
                        </CredentialsSection>

                        <InfoNote>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                            </svg>
                            <span>View the <strong>API Documentation</strong> tab for complete endpoint documentation with request/response examples.</span>
                        </InfoNote>
                    </>
                )}
            </StandardModal>

            {/* Logs Modal */}
            <StandardModal
                isOpen={showLogsModal}
                onClose={() => setShowLogsModal(false)}
                title={`Integration Logs: ${selectedIntegration?.name || ''}`}
                size="large"
            >
                <LogsTable>
                    <LogHeader>
                        <span>Timestamp</span>
                        <span>Status</span>
                        <span>Endpoint</span>
                        <span>Details</span>
                    </LogHeader>
                    {logsLoading ? <LoadingMessage>{t('Settings.loading')}</LoadingMessage> : logsError ? (
                        <ErrorText role="alert">{logsError}</ErrorText>
                    ) : logsData.length > 0 ? (
                        logsData.map((log, index) => (
                            <LogRow key={index}>
                                <span>{new Date(log.created_at).toLocaleString()}</span>
                                <LogStatus $status={log.status_code}>{log.status_code}</LogStatus>
                                <code style={{ fontSize: '0.75rem' }}>{log.endpoint}</code>
                                <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{log.error_message || log.message || '—'}</span>
                            </LogRow>
                        ))
                    ) : (
                        <EmptyState>{t('Settings.Integrations.noLogs')}</EmptyState>
                    )}
                </LogsTable>
            </StandardModal>
        </Container>
    );
};

export default IntegrationsSettings;

// Styled Components
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const FormFooter = styled.div`
    display: flex;
    justify-content: flex-end;
`;


const StatusBadge = styled.span`
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    background: ${({ $active }) => $active ? '#dcfce7' : '#fee2e2'};
    color: ${({ $active }) => $active ? '#166534' : '#991b1b'};
    display: inline-block;
`;

const ActionsContainer = styled.div`
    display: flex;
    gap: 0.375rem;
`;

const IconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    background: ${({ $variant }) => {
        switch ($variant) {
            case 'danger': return '#fee2e2';
            case 'warning': return '#fef3c7';
            case 'success': return '#dcfce7';
            default: return '#f3f4f6';
        }
    }};
    color: ${({ $variant }) => {
        switch ($variant) {
            case 'danger': return '#991b1b';
            case 'warning': return '#92400e';
            case 'success': return '#166534';
            default: return '#374151';
        }
    }};

    &:hover {
        background: ${({ $variant }) => {
            switch ($variant) {
                case 'danger': return '#fecaca';
                case 'warning': return '#fde68a';
                case 'success': return '#bbf7d0';
                default: return '#e5e7eb';
            }
        }};
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

const TypeBadge = styled.span`
    padding: 0.125rem 0.5rem;
    border-radius: 12px;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: capitalize;
`;

const NameCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    span {
        font-weight: 500;
    }
`;

const ApiKeyCell = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    code {
        font-family: 'Monaco', 'Menlo', monospace;
        font-size: 0.75rem;
        background: #f3f4f6;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        color: #374151;
    }
`;

const CopyButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    border: none;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.15s ease;

    &:hover {
        background: #f3f4f6;
        color: #374151;
    }
`;

const CredentialsSection = styled.div`
    margin-bottom: 1.25rem;

    h4 {
        font-size: 0.875rem;
        font-weight: 600;
        color: #374151;
        margin: 0 0 0.75rem 0;
    }
`;

const CredentialRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem;
    background: #f9fafb;
    border-radius: 8px;
    margin-bottom: 0.5rem;

    &:last-child {
        margin-bottom: 0;
    }
`;

const CredentialLabel = styled.span`
    font-size: 0.8125rem;
    color: #6b7280;
`;

const CredentialValue = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    code {
        font-family: 'Monaco', 'Menlo', monospace;
        font-size: 0.75rem;
        background: #e5e7eb;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        color: #1f2937;
        max-width: 300px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const InfoNote = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    padding: 0.75rem 1rem;
    background: #eff6ff;
    border-radius: 8px;
    color: #1e40af;
    font-size: 0.8125rem;
    line-height: 1.5;
    margin-top: 1rem;

    svg {
        flex-shrink: 0;
        margin-top: 0.125rem;
    }

    strong {
        font-weight: 600;
    }
`;

const LogsTable = styled.div`
    max-height: 400px;
    overflow-y: auto;
`;

const LogRow = styled.div`
    display: grid;
    grid-template-columns: 140px 70px 200px 1fr;
    gap: 0.75rem;
    padding: 0.625rem 0.75rem;
    border-bottom: 1px solid #f3f4f6;
    font-size: 0.8125rem;

    &:hover {
        background: #f9fafb;
    }
`;

const LogHeader = styled(LogRow)`
    background: #f3f4f6;
    font-weight: 600;
    color: #374151;
    position: sticky;
    top: 0;
`;

const LogStatus = styled.span`
    display: inline-block;
    padding: 0.125rem 0.5rem;
    border-radius: 4px;
    font-size: 0.6875rem;
    font-weight: 600;
    background: ${({ $status }) => {
        if ($status >= 200 && $status < 300) return '#dcfce7';
        if ($status >= 400 && $status < 500) return '#fef3c7';
        if ($status >= 500) return '#fee2e2';
        return '#e5e7eb';
    }};
    color: ${({ $status }) => {
        if ($status >= 200 && $status < 300) return '#166534';
        if ($status >= 400 && $status < 500) return '#92400e';
        if ($status >= 500) return '#991b1b';
        return '#374151';
    }};
`;
